import { Link } from "@inertiajs/react";
import { ArrowLeft, Terminal } from "lucide-react";
import AppLayout from "../../layouts/AppLayout";

interface Param {
  name: string;
  type: string;
  description: string;
  required: boolean;
}

interface Tool {
  name: string;
  description: string;
  params: Param[];
}

interface Props {
  tools: Tool[];
}

const TOOL_CATEGORIES: Record<string, string> = {
  // Analytics & Reporting
  pipeline_overview:     "Analytics & Reporting",
  stage_velocity:        "Analytics & Reporting",
  interview_candidates:  "Analytics & Reporting",
  weekly_activity:       "Analytics & Reporting",
  dnc_stats:             "Analytics & Reporting",
  skm_performance:       "Analytics & Reporting",
  submissions:           "Analytics & Reporting",
  jobs:                  "Analytics & Reporting",
  new_leads:             "Analytics & Reporting",
  // Candidates
  search_candidates:     "Candidates",
  get_candidate:         "Candidates",
  create_candidate:      "Candidates",
  update_candidate:      "Candidates",
  // DNC
  check_dnc:             "DNC",
  override_dnc:          "DNC",
  // Email & Outreach
  send_one_off_email:    "Email & Outreach",
  send_firm_email:       "Email & Outreach",
  reply_to_email:        "Email & Outreach",
  create_outbound_blast: "Email & Outreach",
  // SKM
  create_skm_blast:      "SKM",
  get_skm_status:        "SKM",
  // Packages
  create_package:        "Packages",
  get_package_status:    "Packages",
  approve_package:       "Packages",
  optimize_resume:       "Packages",
  // Firms
  create_firm:           "Firms",
  get_firm:              "Firms",
  // Leopard
  fetch_leopard_candidates: "Leopard",
  // Settings
  update_setting:        "Settings",
};

const CATEGORY_ORDER = [
  "Analytics & Reporting", "Candidates", "DNC", "Email & Outreach",
  "SKM", "Packages", "Firms", "Leopard", "Settings",
];

function categorizeTool(name: string): string {
  return TOOL_CATEGORIES[name] ?? "Other";
}

export default function McpTools({ tools }: Props) {
  const grouped = tools.reduce<Record<string, Tool[]>>((acc, tool) => {
    const cat = categorizeTool(tool.name);
    (acc[cat] ??= []).push(tool);
    return acc;
  }, {});

  const orderedCategories = [
    ...CATEGORY_ORDER.filter((l) => grouped[l]),
    ...(grouped["Other"] ? ["Other"] : []),
  ];

  return (
    <AppLayout title="MCP Tools">
      <div className="max-w-3xl">
        <Link href="/settings" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeft className="w-4 h-4" /> Settings
        </Link>

        <div className="flex items-center gap-2 mb-6">
          <Terminal className="w-5 h-5 text-gray-400" />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">MCP Tools Reference</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {tools.length} tools available via Claude AI connector
            </p>
          </div>
        </div>

        <div className="space-y-8">
          {orderedCategories.map((category) => (
            <div key={category}>
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{category}</h2>
              <div className="space-y-3">
                {grouped[category].map((tool) => (
                  <ToolCard key={tool.name} tool={tool} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </AppLayout>
  );
}

function ToolCard({ tool }: { tool: Tool }) {
  const requiredParams = tool.params.filter((p) => p.required);
  const optionalParams = tool.params.filter((p) => !p.required);

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <code className="text-sm font-mono font-semibold text-gray-900">{tool.name}</code>
          <p className="text-xs text-gray-500 mt-1 leading-relaxed">{tool.description}</p>
        </div>
      </div>

      {tool.params.length > 0 && (
        <div className="px-4 py-3">
          <div className="space-y-1.5">
            {requiredParams.map((p) => <ParamRow key={p.name} param={p} />)}
            {optionalParams.map((p) => <ParamRow key={p.name} param={p} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function ParamRow({ param }: { param: Param }) {
  return (
    <div className="flex items-baseline gap-2 text-xs">
      <code className="font-mono text-gray-700 shrink-0">{param.name}</code>
      <span className="text-gray-300">·</span>
      <span className="text-gray-400 shrink-0">{param.type}</span>
      {param.required && (
        <span className="text-red-500 shrink-0">required</span>
      )}
      {param.description && (
        <>
          <span className="text-gray-300">—</span>
          <span className="text-gray-500">{param.description}</span>
        </>
      )}
    </div>
  );
}
